/**
 * Return-URL helpers for the login flow (IAS-103).
 *
 * Goal: when an unauthenticated user opens a deep link, send them to /login and,
 * after a successful login, bring them back to the page they originally requested
 * instead of the generic app selector.
 *
 * This works for both login paths WITHOUT any backend change:
 *  - Email/password: the login page reads `?returnUrl=` and navigates there.
 *  - Microsoft SSO: the return URL is forwarded to `GET /auth/saml?returnUrl=`,
 *    which the backend already persists in the SAML session and replays on the
 *    callback (it redirects to `${FRONTEND_URL}${returnUrl}?token=`). So we only
 *    need to pass it along — the round-trip back is handled server-side.
 *
 * Security: the return URL is attacker-influenceable (it travels through query
 * params), so it MUST be sanitized before being used in a navigation to prevent
 * open-redirect attacks. We only ever allow *same-origin relative paths*.
 */

/** Default landing route when there is no (valid) return URL — the app selector. */
export const DEFAULT_RETURN_URL = "/";

/**
 * Validate + normalize a candidate return URL.
 *
 * Returns a safe same-origin relative path, or `null` if the input is missing or
 * not a safe same-origin relative path.
 *
 * Allowed: a single leading slash followed by a path/query/hash, e.g.
 *   "/hrm/employees", "/finance/invoices?status=open", "/crm#tab".
 * Rejected (→ null):
 *   - empty / non-string
 *   - protocol-relative "//evil.com" (browsers treat this as an absolute URL)
 *   - "/\evil.com" or "\\evil.com" (backslashes are normalized to "/" by browsers)
 *   - any scheme URL: "http://", "https://", "javascript:", "data:", "mailto:" ...
 *   - paths that do not start with "/" (e.g. "evil.com", "../x")
 *   - paths containing control characters
 */
export function sanitizeReturnUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;

  let value = raw.trim();
  if (value === "") return null;

  // Decode once if it was passed in encoded form (e.g. read straight from a query
  // string without URLSearchParams decoding). Be defensive against malformed input.
  if (value.includes("%")) {
    try {
      value = decodeURIComponent(value);
    } catch {
      return null;
    }
  }

  // Normalize backslashes to forward slashes — browsers do this, and it would
  // otherwise let "/\evil.com" or "\\evil.com" slip through as an absolute URL.
  const normalized = value.replace(/\\/g, "/");

  // Must be a relative path rooted at a single "/".
  if (!normalized.startsWith("/")) return null;

  // Reject protocol-relative URLs ("//host") which browsers treat as absolute.
  if (normalized.startsWith("//")) return null;

  // Reject control characters (NUL..US and DEL). These can't legitimately appear
  // in a path and could enable parsing/redirect tricks. Normal characters such as
  // "-" and (decoded) spaces are intentionally allowed.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(normalized)) return null;

  return normalized;
}

/**
 * Build the return URL to capture for the *current* location. Combines pathname,
 * search and hash so query params and in-page anchors survive the login round-trip.
 * Never captures the login page itself (avoids bouncing back to /login).
 */
export function captureReturnUrl(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  const { pathname, search = "", hash = "" } = location;
  if (pathname === "/login") return DEFAULT_RETURN_URL;
  const full = `${pathname}${search}${hash}`;
  return sanitizeReturnUrl(full) ?? DEFAULT_RETURN_URL;
}

/**
 * Build the `/login?returnUrl=...` path the redirect-to-login guard should navigate
 * to, capturing the current location. If the captured URL is just the default,
 * return a bare "/login" (no noisy query param).
 */
export function buildLoginPath(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  const returnTo = captureReturnUrl(location);
  if (returnTo === DEFAULT_RETURN_URL) return "/login";
  return `/login?returnUrl=${encodeURIComponent(returnTo)}`;
}

/**
 * Resolve where to navigate after a successful login, given a candidate return URL
 * (from the `returnUrl` query param). Always returns a safe path, falling back to
 * the default app selector when the candidate is missing/unsafe.
 */
export function resolvePostLoginUrl(candidate: unknown): string {
  return sanitizeReturnUrl(candidate) ?? DEFAULT_RETURN_URL;
}
